import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface MultiTimeframeEntry {
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 6: the campaign id, when this entry was added from a campaign option.
   *  Absent for plain frame/custom entries. Fan-out to per-arm predicates is Task 7. */
  campaign?: string
}

export interface MultiTimeframeState {
  entries: MultiTimeframeEntry[]
  add: (e: MultiTimeframeEntry) => void
  remove: (i: number) => void
  clear: () => void
}

const KEY = 'tf_list'

/** Row 22: compare and multi-TF are competing comparison paradigms — entering multi-TF
 *  clears the compare params in the SAME atomic write (no inert compareMode/compareKpi
 *  lingering in the URL). Exit deliberately does NOT restore them. Detection uses the URL
 *  itself (params.has) — an app-written tf_list is never present-but-empty (writeEntries
 *  deletes on empty), so has(KEY) ⟺ multi-TF active. Keys per useComparisonState
 *  (compare = the legacy pre-mode param). */
const COMPARE_KEYS = ['compareMode', 'compareKpi', 'compareFrom', 'compareTo', 'compare']

export function useMultiTimeframeState(): MultiTimeframeState {
  const [searchParams, setSearchParams] = useSearchParams()

  const entries = useMemo<MultiTimeframeEntry[]>(() => {
    const raw = searchParams.get(KEY)
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) return []
      return (
        parsed
          .filter(
            (e): e is MultiTimeframeEntry =>
              typeof e === 'object' &&
              e !== null &&
              typeof e.label === 'string' &&
              typeof e.from === 'string' &&
              typeof e.to === 'string',
          )
          // A non-string campaign (malformed/legacy-adjacent URL edit) is dropped from the
          // entry, not the whole entry — the entry itself still parses.
          .map((e) =>
            typeof e.campaign === 'string' ? e : { label: e.label, from: e.from, to: e.to },
          )
      )
    } catch {
      return []
    }
  }, [searchParams])

  const writeEntries = useCallback(
    (next: MultiTimeframeEntry[]) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          const entering = next.length > 0 && !params.has(KEY)
          if (next.length === 0) params.delete(KEY)
          else params.set(KEY, JSON.stringify(next))
          if (entering) for (const k of COMPARE_KEYS) params.delete(k)
          return params
        },
        { replace: false },
      )
    },
    [setSearchParams],
  )

  const add = useCallback(
    (e: MultiTimeframeEntry) => writeEntries([...entries, e]),
    [entries, writeEntries],
  )
  const remove = useCallback(
    (i: number) => writeEntries(entries.filter((_, idx) => idx !== i)),
    [entries, writeEntries],
  )
  const clear = useCallback(() => writeEntries([]), [writeEntries])

  return { entries, add, remove, clear }
}
